import { useEffect, useMemo, useState } from 'react';
import {
  Banner,
  Button,
  Card,
  Checkbox,
  Icon,
  Page,
  SkeletonBodyText,
  Text,
  TextField,
} from '@shopify/polaris';
import {
  ClipboardCheckIcon,
  ClipboardIcon,
  HideIcon,
  ViewIcon,
} from '@shopify/polaris-icons';
import { MenuSelect } from '../components/MenuSelect';
import { StatusBadge } from '../components/StatusBadge';
import { presentDependencyMessage, presentSettingsFieldLabel } from '../lib/settingsPresentation';

const TARGET = 'pay-web';
const TOKEN_SECRET_KEY = 'NOVA_POSHTA_API_KEY';
const STORED_TOKEN_MASK = '••••••••••••••••';

const NOVA_KEYS = [
  'NOVA_POSHTA_API_URL',
  'NOVA_POSHTA_API_KEY',
  'NOVA_POSHTA_TIMEOUT_MS',
  'NOVA_POSHTA_TTN_ENABLED',
  'NOVA_POSHTA_TTN_DRY_RUN',
  'NOVA_POSHTA_TTN_CREATION_MODE',
  'NOVA_POSHTA_TTN_AUTO_RETRY_FAILED_ENABLED',
  'NOVA_POSHTA_TTN_AUTO_RETRY_FAILED_MAX_AGE_HOURS',
  'NOVA_POSHTA_TTN_AUTO_RETRY_FAILED_DELAY_MINUTES',
  'NOVA_POSHTA_TTN_AUTO_RETRY_FAILED_BATCH_SIZE',
  'NOVA_POSHTA_DELIVERY_BRANCH_ENABLED',
  'NOVA_POSHTA_DELIVERY_POSTOMAT_ENABLED',
  'NOVA_POSHTA_DELIVERY_COURIER_ENABLED',
  'NOVA_POSHTA_WAREHOUSE_BRANCH_ENABLED',
  'NOVA_POSHTA_WAREHOUSE_LIMITED_BRANCH_ENABLED',
  'NOVA_POSHTA_WAREHOUSE_CARGO_ENABLED',
  'NOVA_POSHTA_WAREHOUSE_POSTOMAT_ENABLED',
  'NOVA_POSHTA_WAREHOUSE_PRIVATBANK_POSTOMAT_ENABLED',
  'NOVA_POSHTA_COD_ENABLED',
  'NOVA_POSHTA_FREE_SHIPPING_THRESHOLD_CENTS',
  'NOVA_POSHTA_PRICE_CACHE_ENABLED',
  'NOVA_POSHTA_PRICE_CACHE_TTL_MINUTES',
  'NOVA_POSHTA_PRICE_CACHE_STALE_MINUTES',
  'NOVA_POSHTA_FALLBACK_BRANCH_PRICE_CENTS',
  'NOVA_POSHTA_FALLBACK_POSTOMAT_PRICE_CENTS',
  'NOVA_POSHTA_FALLBACK_COURIER_PRICE_CENTS',
  'NOVA_POSHTA_DELIVERY_PAYER_TYPE',
  'NOVA_POSHTA_PAYMENT_METHOD',
  'NOVA_POSHTA_SENDER_DISPATCH_TYPE',
  'NOVA_POSHTA_CARGO_TYPE',
  'NOVA_POSHTA_SENDER_REF',
  'NOVA_POSHTA_SENDER_CITY_REF',
  'NOVA_POSHTA_SENDER_ADDRESS_REF',
  'NOVA_POSHTA_SENDER_CONTACT_REF',
  'NOVA_POSHTA_SENDER_PHONE',
  'NOVA_POSHTA_DEFAULT_WEIGHT_KG',
  'NOVA_POSHTA_DEFAULT_SEATS_AMOUNT',
  'NOVA_POSHTA_DEFAULT_LENGTH_CM',
  'NOVA_POSHTA_DEFAULT_WIDTH_CM',
  'NOVA_POSHTA_DEFAULT_HEIGHT_CM',
  'NOVA_POSHTA_DEFAULT_COST_CENTS',
  'NOVA_POSHTA_DEFAULT_DESCRIPTION',
];

const SENDER_KEYS = [
  'NOVA_POSHTA_SENDER_REF',
  'NOVA_POSHTA_SENDER_CITY_REF',
  'NOVA_POSHTA_SENDER_ADDRESS_REF',
  'NOVA_POSHTA_SENDER_CONTACT_REF',
  'NOVA_POSHTA_SENDER_PHONE',
];

const TTN_KEYS = [
  'NOVA_POSHTA_TTN_ENABLED',
  'NOVA_POSHTA_TTN_DRY_RUN',
  'NOVA_POSHTA_TTN_CREATION_MODE',
  'NOVA_POSHTA_TTN_AUTO_RETRY_FAILED_ENABLED',
  'NOVA_POSHTA_TTN_AUTO_RETRY_FAILED_MAX_AGE_HOURS',
  'NOVA_POSHTA_TTN_AUTO_RETRY_FAILED_DELAY_MINUTES',
  'NOVA_POSHTA_TTN_AUTO_RETRY_FAILED_BATCH_SIZE',
  'NOVA_POSHTA_DELIVERY_BRANCH_ENABLED',
  'NOVA_POSHTA_DELIVERY_POSTOMAT_ENABLED',
  'NOVA_POSHTA_DELIVERY_COURIER_ENABLED',
  'NOVA_POSHTA_WAREHOUSE_BRANCH_ENABLED',
  'NOVA_POSHTA_WAREHOUSE_LIMITED_BRANCH_ENABLED',
  'NOVA_POSHTA_WAREHOUSE_CARGO_ENABLED',
  'NOVA_POSHTA_WAREHOUSE_POSTOMAT_ENABLED',
  'NOVA_POSHTA_WAREHOUSE_PRIVATBANK_POSTOMAT_ENABLED',
  'NOVA_POSHTA_COD_ENABLED',
  'NOVA_POSHTA_FREE_SHIPPING_THRESHOLD_CENTS',
  'NOVA_POSHTA_PRICE_CACHE_ENABLED',
  'NOVA_POSHTA_PRICE_CACHE_TTL_MINUTES',
  'NOVA_POSHTA_PRICE_CACHE_STALE_MINUTES',
  'NOVA_POSHTA_FALLBACK_BRANCH_PRICE_CENTS',
  'NOVA_POSHTA_FALLBACK_POSTOMAT_PRICE_CENTS',
  'NOVA_POSHTA_FALLBACK_COURIER_PRICE_CENTS',
  'NOVA_POSHTA_DELIVERY_PAYER_TYPE',
  'NOVA_POSHTA_PAYMENT_METHOD',
  'NOVA_POSHTA_SENDER_DISPATCH_TYPE',
  'NOVA_POSHTA_CARGO_TYPE',
  'NOVA_POSHTA_DEFAULT_WEIGHT_KG',
  'NOVA_POSHTA_DEFAULT_SEATS_AMOUNT',
  'NOVA_POSHTA_DEFAULT_LENGTH_CM',
  'NOVA_POSHTA_DEFAULT_WIDTH_CM',
  'NOVA_POSHTA_DEFAULT_HEIGHT_CM',
  'NOVA_POSHTA_DEFAULT_COST_CENTS',
  'NOVA_POSHTA_DEFAULT_DESCRIPTION',
  'NOVA_POSHTA_API_URL',
  'NOVA_POSHTA_TIMEOUT_MS',
];

const TTN_MODE_OPTIONS = [
  { label: 'Вимкнено', value: 'disabled', helpText: 'Backend не створює ТТН автоматично.' },
  { label: 'Тестовий режим', value: 'test', helpText: 'Backend готує payload, але не створює реальну ТТН.' },
  { label: 'Бойовий режим', value: 'live', helpText: 'Backend має право створювати реальні ТТН через API.' },
];

const CREATION_MODE_OPTIONS = [
  { label: 'Тільки вручну', value: 'manual', helpText: 'Автоматика не запускає створення; оператор створює ТТН сам.' },
  { label: 'Після підтвердження оплати', value: 'after_payment', helpText: 'Для LiqPay після success; для післяплати можна запускати після замовлення.' },
  { label: 'Одразу після створення замовлення', value: 'on_order_create', helpText: 'ТТН створюється без очікування оплати.' },
];

const DELIVERY_TYPE_KEYS = [
  'NOVA_POSHTA_DELIVERY_BRANCH_ENABLED',
  'NOVA_POSHTA_DELIVERY_POSTOMAT_ENABLED',
  'NOVA_POSHTA_DELIVERY_COURIER_ENABLED',
];

const WAREHOUSE_TYPE_KEYS = [
  'NOVA_POSHTA_WAREHOUSE_BRANCH_ENABLED',
  'NOVA_POSHTA_WAREHOUSE_LIMITED_BRANCH_ENABLED',
  'NOVA_POSHTA_WAREHOUSE_CARGO_ENABLED',
  'NOVA_POSHTA_WAREHOUSE_POSTOMAT_ENABLED',
  'NOVA_POSHTA_WAREHOUSE_PRIVATBANK_POSTOMAT_ENABLED',
];

const PAYER_TYPE_OPTIONS = [
  { label: 'Одержувач', value: 'Recipient', helpText: 'Доставку оплачує покупець.' },
  { label: 'Відправник', value: 'Sender', helpText: 'Доставку бере на себе магазин.' },
];

const PAYMENT_FORM_OPTIONS = [
  { label: 'Готівка', value: 'Cash', helpText: 'Форма оплати доставки у Новій Пошті: Cash.' },
  { label: 'Безготівковий', value: 'NonCash', helpText: 'Форма оплати доставки у Новій Пошті: NonCash.' },
];

const SENDER_DISPATCH_OPTIONS = [
  { label: 'З відділення Нової Пошти', value: 'warehouse', helpText: 'ServiceType починається з Warehouse.' },
  { label: 'З адреси магазину', value: 'doors', helpText: 'ServiceType починається з Doors.' },
];

const CARGO_TYPE_OPTIONS = [
  { label: 'Посилка', value: 'Parcel', helpText: 'Стандартний тип для одягу та більшості замовлень.' },
  { label: 'Вантаж', value: 'Cargo', helpText: 'Для більших або вантажних відправлень.' },
  { label: 'Документи', value: 'Documents', helpText: 'Для документів.' },
  { label: 'Шини-диски', value: 'TiresWheels', helpText: 'Спеціальний тип вантажу Нової Пошти.' },
  { label: 'Палети', value: 'Pallet', helpText: 'Палетні відправлення.' },
];

function settingsByKey(snapshot: any) {
  const settings = snapshot?.targets?.payWeb?.settings || [];
  return Object.fromEntries(settings.map((setting: any) => [setting.key, setting]));
}

function isSecretConfigured(setting: any) {
  return Boolean(setting?.currentValue?.configured);
}

function currentEditableValue(setting: any) {
  if (!setting) return '';
  if (setting.secret) return '';
  if (setting.type === 'bool') return Boolean(setting.currentValue);
  if (setting.type === 'int') return Number(setting.currentValue ?? setting.defaultValue ?? 0);
  return String(setting.currentValue ?? setting.defaultValue ?? '');
}

function buildDrafts(settingsMap: Record<string, any>) {
  return Object.fromEntries(
    NOVA_KEYS.map((key) => [key, currentEditableValue(settingsMap[key])])
  );
}

function serializeValue(setting: any, value: any) {
  if (setting?.type === 'bool') return value ? 'true' : 'false';
  if (setting?.type === 'int') return String(Number(value ?? 0));
  return String(value ?? '');
}

function buildChanges(settingsMap: Record<string, any>, drafts: Record<string, any>, keys: string[]) {
  const changes: Record<string, any> = {};

  keys.forEach((key) => {
    const setting = settingsMap[key];
    if (!setting) return;

    const nextValue = drafts[key];
    if (setting.secret) {
      if (String(nextValue || '').trim()) changes[key] = nextValue;
      return;
    }

    if (serializeValue(setting, currentEditableValue(setting)) !== serializeValue(setting, nextValue)) {
      changes[key] = nextValue;
    }
  });

  return changes;
}

function directoryLabel(item: any) {
  return item?.label || item?.description || item?.fullName || item?.ref || '—';
}

function directoryOptions(items: any[] = []) {
  return items.map((item) => ({
    label: directoryLabel(item),
    value: item.ref,
    helpText: item.city ? item.city : item.phone ? item.phone : item.ref,
  }));
}

function settingLabel(key: string) {
  return presentSettingsFieldLabel(key);
}

function errorText(error: unknown, fallback: string) {
  return presentDependencyMessage(error instanceof Error ? error.message : fallback);
}

function resolveTtnOperationalMode(drafts: Record<string, any>) {
  const enabled = Boolean(drafts.NOVA_POSHTA_TTN_ENABLED);
  const dryRun = drafts.NOVA_POSHTA_TTN_DRY_RUN !== false;

  if (!enabled) return 'disabled';
  return dryRun ? 'test' : 'live';
}

function centsToUahInput(value: any) {
  const cents = Number(value ?? 0);
  if (!Number.isFinite(cents) || cents <= 0) return '0';
  return String(cents / 100);
}

function uahInputToCents(value: string) {
  const normalized = String(value || '').replace(',', '.').trim();
  const parsed = Number.parseFloat(normalized);
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return Math.round(parsed * 100);
}

function ttnModeStatus(mode: string) {
  if (mode === 'live') {
    return {
      status: 'ok',
      label: 'Бойовий',
      text: 'Реальні ТТН можуть створюватися через Nova Poshta API.',
    };
  }
  if (mode === 'test') {
    return {
      status: 'warning',
      label: 'Тестовий',
      text: 'Backend готує дані ТТН, але не створює реальну накладну.',
    };
  }
  return {
    status: 'disabled',
    label: 'Вимкнено',
    text: 'Автоматичне створення ТТН вимкнене.',
  };
}

function formatSecretState(setting: any) {
  if (!isSecretConfigured(setting)) return 'Токен ще не збережено';
  const fingerprint = setting.currentValue?.fingerprint;
  return fingerprint ? `Токен збережено · контрольний код ${fingerprint}` : 'Токен збережено';
}

async function writeClipboard(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', 'true');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
}

export function NovaPoshtaSettingsPage({ api, onRefreshOverview, notify, readOnly = false }: any) {
  const [snapshot, setSnapshot] = useState<any>(null);
  const [drafts, setDrafts] = useState<Record<string, any>>({});
  const [tokenDraft, setTokenDraft] = useState('');
  const [revealedToken, setRevealedToken] = useState('');
  const [tokenVisible, setTokenVisible] = useState(false);
  const [tokenActionBusy, setTokenActionBusy] = useState(false);
  const [tokenCopied, setTokenCopied] = useState(false);
  const [directory, setDirectory] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [savingToken, setSavingToken] = useState(false);
  const [savingSender, setSavingSender] = useState(false);
  const [savingRules, setSavingRules] = useState(false);
  const [directoryLoading, setDirectoryLoading] = useState(false);
  const [error, setError] = useState('');
  const [directoryError, setDirectoryError] = useState('');

  const settingsMap = useMemo(() => settingsByKey(snapshot), [snapshot]);
  const apiKeySetting = settingsMap.NOVA_POSHTA_API_KEY;
  const apiKeyConfigured = isSecretConfigured(apiKeySetting);

  const loadDirectory = async (senderRef = '') => {
    setDirectoryLoading(true);
    setDirectoryError('');
    try {
      const result = await api.getNovaPoshtaSenderDirectory(senderRef || undefined);
      setDirectory(result);

      const selectedSender = result?.selectedSender;
      const firstContact = result?.contacts?.[0];
      const firstAddress = result?.addresses?.[0];

      setDrafts((current) => ({
        ...current,
        ...(selectedSender?.ref ? { NOVA_POSHTA_SENDER_REF: current.NOVA_POSHTA_SENDER_REF || selectedSender.ref } : {}),
        ...(selectedSender?.cityRef ? { NOVA_POSHTA_SENDER_CITY_REF: current.NOVA_POSHTA_SENDER_CITY_REF || selectedSender.cityRef } : {}),
        ...(firstContact?.ref ? { NOVA_POSHTA_SENDER_CONTACT_REF: current.NOVA_POSHTA_SENDER_CONTACT_REF || firstContact.ref } : {}),
        ...(firstContact?.phone ? { NOVA_POSHTA_SENDER_PHONE: current.NOVA_POSHTA_SENDER_PHONE || firstContact.phone } : {}),
        ...(firstAddress?.ref ? { NOVA_POSHTA_SENDER_ADDRESS_REF: current.NOVA_POSHTA_SENDER_ADDRESS_REF || firstAddress.ref } : {}),
      }));
    } catch (nextError) {
      const message = errorText(nextError, 'Не вдалося завантажити довідник Нової Пошти');
      setDirectoryError(message);
      notify?.({ tone: 'critical', title: 'Нова Пошта недоступна', message });
    } finally {
      setDirectoryLoading(false);
    }
  };

  const refresh = async ({ loadRemote = true } = {}) => {
    setLoading(true);
    try {
      const nextSnapshot = await api.getSettings();
      const nextSettings = settingsByKey(nextSnapshot);
      const nextDrafts = buildDrafts(nextSettings);
      setSnapshot(nextSnapshot);
      setDrafts(nextDrafts);
      setRevealedToken('');
      setTokenVisible(false);
      setTokenCopied(false);
      setError('');

      if (loadRemote && isSecretConfigured(nextSettings.NOVA_POSHTA_API_KEY)) {
        await loadDirectory(String(nextDrafts.NOVA_POSHTA_SENDER_REF || ''));
      }
    } catch (nextError) {
      const message = errorText(nextError, 'Не вдалося завантажити налаштування Нової Пошти');
      setError(message);
      notify?.({ tone: 'critical', title: 'Нова Пошта недоступна', message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const updateDraft = (key: string, value: any) => {
    setDrafts((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const applyChanges = async ({
    changes,
    title,
    setBusy,
  }: {
    changes: Record<string, any>;
    title: string;
    setBusy: (busy: boolean) => void;
  }) => {
    if (Object.keys(changes).length === 0) {
      notify?.({ tone: 'info', title: 'Немає змін', message: 'Ці налаштування вже відповідають поточному стану.' });
      return;
    }

    setBusy(true);
    try {
      const preview = await api.previewSettings(TARGET, changes);
      await api.applySettings(TARGET, changes, preview.revision);
      notify?.({ tone: 'success', title, message: 'Зміни збережено і сервіс перезапущено.' });
      setError('');
      await refresh();
      await onRefreshOverview?.();
    } catch (nextError) {
      const message = errorText(nextError, 'Не вдалося зберегти налаштування Нової Пошти');
      setError(message);
      notify?.({ tone: 'critical', title: 'Зміни не збережено', message });
    } finally {
      setBusy(false);
    }
  };

  const saveToken = async () => {
    const token = tokenDraft.trim();
    if (!token) {
      setError('Вставте API токен Нової Пошти.');
      return;
    }

    await applyChanges({
      changes: { NOVA_POSHTA_API_KEY: token },
      title: 'Токен Нової Пошти збережено',
      setBusy: setSavingToken,
    });
    setTokenDraft('');
  };

  const revealToken = async () => {
    if (tokenDraft.trim()) {
      setTokenVisible((current) => !current);
      return tokenDraft.trim();
    }

    if (revealedToken) {
      setTokenVisible((current) => !current);
      return revealedToken;
    }

    setTokenActionBusy(true);
    try {
      const result = await api.revealSettingSecret(TARGET, TOKEN_SECRET_KEY);
      const value = result?.value || '';
      setRevealedToken(value);
      setTokenVisible(true);
      return value;
    } catch (nextError) {
      const message = errorText(nextError, 'Не вдалося показати токен Нової Пошти');
      setError(message);
      notify?.({ tone: 'critical', title: 'Токен не відкрився', message });
      throw nextError;
    } finally {
      setTokenActionBusy(false);
    }
  };

  const readStoredTokenForCopy = async () => {
    if (revealedToken) return revealedToken;

    const result = await api.revealSettingSecret(TARGET, TOKEN_SECRET_KEY);
    const value = result?.value || '';
    setRevealedToken(value);
    return value;
  };

  const copyToken = async () => {
    setTokenActionBusy(true);
    try {
      const value = tokenDraft.trim() || await readStoredTokenForCopy();
      if (!value) {
        notify?.({ tone: 'info', title: 'Токен порожній', message: 'API токен Нової Пошти ще не збережено.' });
        return;
      }

      await writeClipboard(value);
      setTokenCopied(true);
      window.setTimeout(() => setTokenCopied(false), 1300);
      notify?.({ tone: 'success', title: 'Токен скопійовано' });
    } catch (nextError) {
      const message = errorText(nextError, 'Не вдалося скопіювати токен Нової Пошти');
      notify?.({ tone: 'critical', title: 'Не вдалося скопіювати', message });
    } finally {
      setTokenActionBusy(false);
    }
  };

  const tokenFieldValue = tokenDraft || (
    apiKeyConfigured
      ? tokenVisible
        ? revealedToken
        : STORED_TOKEN_MASK
      : ''
  );

  const tokenFieldType = tokenVisible ? 'text' : 'password';
  const ttnMode = resolveTtnOperationalMode(drafts);
  const ttnModeMeta = ttnModeStatus(ttnMode);

  const handleTokenChange = (value: string) => {
    if (!tokenDraft && apiKeyConfigured && value.startsWith(STORED_TOKEN_MASK)) {
      setTokenDraft(value.slice(STORED_TOKEN_MASK.length).trimStart());
      return;
    }

    setTokenDraft(value);
    setTokenCopied(false);
  };

  const saveSender = async () => {
    await applyChanges({
      changes: buildChanges(settingsMap, drafts, SENDER_KEYS),
      title: 'Відправника Нової Пошти збережено',
      setBusy: setSavingSender,
    });
  };

  const saveRules = async () => {
    await applyChanges({
      changes: buildChanges(settingsMap, drafts, TTN_KEYS),
      title: 'Правила Нової Пошти збережено',
      setBusy: setSavingRules,
    });
  };

  const handleTtnModeChange = (mode: string) => {
    setDrafts((current) => ({
      ...current,
      NOVA_POSHTA_TTN_ENABLED: mode !== 'disabled',
      NOVA_POSHTA_TTN_DRY_RUN: mode !== 'live',
    }));
  };

  const handleSenderChange = async (senderRef: string) => {
    const sender = (directory?.senders || []).find((entry: any) => entry.ref === senderRef);
    updateDraft('NOVA_POSHTA_SENDER_REF', senderRef);
    if (sender?.cityRef) updateDraft('NOVA_POSHTA_SENDER_CITY_REF', sender.cityRef);
    updateDraft('NOVA_POSHTA_SENDER_CONTACT_REF', '');
    updateDraft('NOVA_POSHTA_SENDER_ADDRESS_REF', '');
    await loadDirectory(senderRef);
  };

  const handleContactChange = (contactRef: string) => {
    const contact = (directory?.contacts || []).find((entry: any) => entry.ref === contactRef);
    updateDraft('NOVA_POSHTA_SENDER_CONTACT_REF', contactRef);
    if (contact?.phone) updateDraft('NOVA_POSHTA_SENDER_PHONE', contact.phone);
  };

  const handleAddressChange = (addressRef: string) => {
    const address = (directory?.addresses || []).find((entry: any) => entry.ref === addressRef);
    updateDraft('NOVA_POSHTA_SENDER_ADDRESS_REF', addressRef);
    if (address?.cityRef) updateDraft('NOVA_POSHTA_SENDER_CITY_REF', address.cityRef);
  };

  const handleFreeShippingThresholdChange = (value: string) => {
    updateDraft('NOVA_POSHTA_FREE_SHIPPING_THRESHOLD_CENTS', uahInputToCents(value));
  };

  return (
    <Page
      title="Нова Пошта"
      subtitle="API токен, відправник і правила створення ТТН"
      primaryAction={{ content: 'Оновити', onAction: () => refresh(), loading }}
    >
      <div className="admin-stack">
        {error ? (
          <Banner tone="critical" title="Налаштування Нової Пошти недоступні">
            <p>{error}</p>
          </Banner>
        ) : null}
        {readOnly ? (
          <Banner tone="info" title="Роль перегляду">
            <p>Налаштування Нової Пошти відкриті тільки для перегляду. Збереження токена і змін вимкнено.</p>
          </Banner>
        ) : null}

        {loading && !snapshot ? (
          <Card>
            <div className="admin-stack">
              <SkeletonBodyText lines={6} />
            </div>
          </Card>
        ) : null}

        {snapshot ? (
          <fieldset className="admin-fieldset-reset" disabled={readOnly}>
            <div className="admin-stack">
              <Card>
                <div className="admin-stack">
                  <div className="admin-section-head">
                    <div>
                      <Text as="h2" variant="headingMd">API токен</Text>
                      <Text as="p" variant="bodyMd" tone="subdued">
                        Спочатку збережіть токен. Після цього адмінка відкриє довідники відправника, контактів і адрес.
                      </Text>
                    </div>
                    <StatusBadge status={apiKeyConfigured ? 'ok' : 'warning'} label={apiKeyConfigured ? 'Підключено' : 'Немає токена'} />
                  </div>
                  <Text as="p" variant="bodySm" tone="subdued">{formatSecretState(apiKeySetting)}</Text>
                  <div className="admin-nova-token-row">
                    <div className="admin-nova-token-field">
                      <TextField
                        autoComplete="off"
                        label="API токен Нової Пошти"
                        type={tokenFieldType}
                        value={tokenFieldValue}
                        onChange={handleTokenChange}
                        placeholder={apiKeyConfigured ? 'Вставте новий токен, якщо треба замінити' : 'Вставте API токен'}
                      />
                      <div className="admin-nova-token-tools" aria-label="Дії з API токеном">
                        <button
                          type="button"
                          className="admin-icon-button"
                          disabled={(!apiKeyConfigured && !tokenDraft.trim()) || tokenActionBusy}
                          onClick={revealToken}
                          title={tokenVisible ? 'Сховати токен' : 'Показати токен'}
                          aria-label={tokenVisible ? 'Сховати токен' : 'Показати токен'}
                        >
                          <Icon source={tokenVisible ? HideIcon : ViewIcon} />
                        </button>
                        <button
                          type="button"
                          className={`admin-icon-button admin-copy-button ${tokenCopied ? 'is-copied' : ''}`}
                          disabled={(!apiKeyConfigured && !tokenDraft.trim()) || tokenActionBusy}
                          onClick={copyToken}
                          title="Скопіювати токен"
                          aria-label="Скопіювати токен"
                        >
                          <Icon source={tokenCopied ? ClipboardCheckIcon : ClipboardIcon} />
                        </button>
                      </div>
                    </div>
                    <div className="admin-nova-token-action">
                      <Button
                        size="slim"
                        variant="primary"
                        loading={savingToken}
                        disabled={!tokenDraft.trim()}
                        onClick={saveToken}
                      >
                        Зберегти токен
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>

              {apiKeyConfigured ? (
                <>
                  <Card>
                    <div className="admin-stack">
                      <div className="admin-section-head">
                        <div>
                          <Text as="h2" variant="headingMd">Дані з кабінету</Text>
                          <Text as="p" variant="bodyMd" tone="subdued">
                            Дані підтягуються з кабінету Нової Пошти за збереженим токеном.
                          </Text>
                        </div>
                        <Button loading={directoryLoading} onClick={() => loadDirectory(String(drafts.NOVA_POSHTA_SENDER_REF || ''))}>
                          Підтягнути з API
                        </Button>
                      </div>

                      {directoryError ? (
                        <Banner tone="critical" title="Нова Пошта не відповіла">
                          <p>{directoryError}</p>
                        </Banner>
                      ) : null}

                      {directory ? (
                        <div className="admin-nova-directory__summary">
                          <StatusBadge status="ok" label="Довідник завантажено" />
                          <Text as="p" variant="bodySm" tone="subdued">
                            Відправників: {directory.senders?.length || 0} · контактів: {directory.contacts?.length || 0} · адрес: {directory.addresses?.length || 0}
                          </Text>
                        </div>
                      ) : null}
                    </div>
                  </Card>

                  <Card>
                    <div className="admin-stack">
                      <div>
                        <Text as="h2" variant="headingMd">Відправник</Text>
                        <Text as="p" variant="bodyMd" tone="subdued">
                          Ці дані підуть у майбутнє створення ТТН як сторона відправника.
                        </Text>
                      </div>

                      <div className="admin-grid admin-grid--two">
                        <MenuSelect
                          label="Відправник"
                          options={directoryOptions(directory?.senders || [])}
                          value={String(drafts.NOVA_POSHTA_SENDER_REF || '')}
                          onChange={handleSenderChange}
                        />
                        <MenuSelect
                          label="Контакт відправника"
                          options={directoryOptions(directory?.contacts || [])}
                          value={String(drafts.NOVA_POSHTA_SENDER_CONTACT_REF || '')}
                          onChange={handleContactChange}
                        />
                      </div>
                      <div className="admin-grid admin-grid--two">
                        <MenuSelect
                          label="Адреса відправлення"
                          options={directoryOptions(directory?.addresses || [])}
                          value={String(drafts.NOVA_POSHTA_SENDER_ADDRESS_REF || '')}
                          onChange={handleAddressChange}
                        />
                        <TextField
                          autoComplete="off"
                          label={settingLabel('NOVA_POSHTA_SENDER_PHONE')}
                          value={String(drafts.NOVA_POSHTA_SENDER_PHONE || '')}
                          onChange={(value) => updateDraft('NOVA_POSHTA_SENDER_PHONE', value)}
                        />
                      </div>
                      <details className="admin-advanced-details">
                        <summary>Ref-и для технічної перевірки</summary>
                        <div className="admin-grid admin-grid--two">
                          {SENDER_KEYS.filter((key) => key !== 'NOVA_POSHTA_SENDER_PHONE').map((key) => (
                            <TextField
                              key={key}
                              autoComplete="off"
                              label={settingLabel(key)}
                              value={String(drafts[key] || '')}
                              onChange={(value) => updateDraft(key, value)}
                            />
                          ))}
                        </div>
                      </details>
                      <div className="admin-inline-actions">
                        <Button variant="primary" loading={savingSender} onClick={saveSender}>
                          Зберегти відправника
                        </Button>
                      </div>
                    </div>
                  </Card>

                  <Card>
                    <div className="admin-stack">
                      <div className="admin-section-head">
                        <div>
                          <Text as="h2" variant="headingMd">Режим створення ТТН</Text>
                          <Text as="p" variant="bodyMd" tone="subdued">
                            Режим керує двома технічними прапорцями: чи дозволено створення і чи це тест без реальної накладної.
                          </Text>
                        </div>
                        <StatusBadge status={ttnModeMeta.status} label={ttnModeMeta.label} />
                      </div>
                      <div className="admin-grid admin-grid--two">
                        <MenuSelect
                          label="Режим"
                          options={TTN_MODE_OPTIONS}
                          value={ttnMode}
                          onChange={handleTtnModeChange}
                        />
                        <MenuSelect
                          label={settingLabel('NOVA_POSHTA_TTN_CREATION_MODE')}
                          options={CREATION_MODE_OPTIONS}
                          value={String(drafts.NOVA_POSHTA_TTN_CREATION_MODE || 'manual')}
                          onChange={(value) => updateDraft('NOVA_POSHTA_TTN_CREATION_MODE', value)}
                        />
                      </div>
                      <Text as="p" variant="bodySm" tone="subdued">{ttnModeMeta.text}</Text>
                      <details className="admin-advanced-details">
                        <summary>Що означають технічні прапорці</summary>
                        <div className="admin-grid admin-grid--two">
                          <Text as="p" variant="bodySm" tone="subdued">
                            {settingLabel('NOVA_POSHTA_TTN_ENABLED')}: {Boolean(drafts.NOVA_POSHTA_TTN_ENABLED) ? 'увімкнено' : 'вимкнено'}
                          </Text>
                          <Text as="p" variant="bodySm" tone="subdued">
                            {settingLabel('NOVA_POSHTA_TTN_DRY_RUN')}: {drafts.NOVA_POSHTA_TTN_DRY_RUN !== false ? 'увімкнено' : 'вимкнено'}
                          </Text>
                        </div>
                      </details>
                    </div>
                  </Card>

                  <Card>
                    <div className="admin-stack">
                      <div>
                        <Text as="h2" variant="headingMd">Доставка Новою Поштою</Text>
                        <Text as="p" variant="bodyMd" tone="subdued">
                          Ці правила впливають на custom checkout і довідник точок видачі.
                        </Text>
                      </div>
                      <div className="admin-grid admin-grid--three">
                        {DELIVERY_TYPE_KEYS.map((key) => (
                          <Checkbox
                            key={key}
                            label={settingLabel(key)}
                            checked={Boolean(drafts[key])}
                            onChange={(checked) => updateDraft(key, checked)}
                          />
                        ))}
                      </div>
                      <div>
                        <Text as="h3" variant="headingSm">Дозволені типи відділень і поштоматів</Text>
                        <Text as="p" variant="bodySm" tone="subdued">
                          API Нової Пошти повертає ці типи через довідник складів; вимкнені типи не потраплятимуть у результати autocomplete.
                        </Text>
                      </div>
                      <div className="admin-grid admin-grid--two">
                        {WAREHOUSE_TYPE_KEYS.map((key) => (
                          <Checkbox
                            key={key}
                            label={settingLabel(key)}
                            checked={Boolean(drafts[key])}
                            onChange={(checked) => updateDraft(key, checked)}
                          />
                        ))}
                      </div>
                    </div>
                  </Card>

                  <Card>
                    <div className="admin-stack">
                      <div>
                        <Text as="h2" variant="headingMd">Оплата доставки</Text>
                        <Text as="p" variant="bodyMd" tone="subdued">
                          Custom checkout додає розраховану доставку Нової Пошти до суми замовлення до порогу. Від порогу доставка безкоштовна для покупця, а послуги Нової Пошти оплачує магазин.
                        </Text>
                      </div>
                      <TextField
                        autoComplete="off"
                        label="Безкоштовна доставка від, грн"
                        type="number"
                        value={centsToUahInput(drafts.NOVA_POSHTA_FREE_SHIPPING_THRESHOLD_CENTS)}
                        onChange={handleFreeShippingThresholdChange}
                        min={0}
                      />
                      <Checkbox
                        label={settingLabel('NOVA_POSHTA_COD_ENABLED')}
                        checked={Boolean(drafts.NOVA_POSHTA_COD_ENABLED)}
                        onChange={(checked) => updateDraft('NOVA_POSHTA_COD_ENABLED', checked)}
                      />
                      <div>
                        <Text as="h3" variant="headingSm">Кеш тарифів і fallback-ціни</Text>
                        <Text as="p" variant="bodySm" tone="subdued">
                          Checkout спершу бере актуальний тариф з API Нової Пошти, потім короткий кеш, потім старий кеш при тимчасовій помилці, і тільки після цього резервну ціну.
                        </Text>
                      </div>
                      <Checkbox
                        label={settingLabel('NOVA_POSHTA_PRICE_CACHE_ENABLED')}
                        checked={drafts.NOVA_POSHTA_PRICE_CACHE_ENABLED !== false}
                        onChange={(checked) => updateDraft('NOVA_POSHTA_PRICE_CACHE_ENABLED', checked)}
                      />
                      <div className="admin-grid admin-grid--two">
                        <TextField
                          autoComplete="off"
                          label={settingLabel('NOVA_POSHTA_PRICE_CACHE_TTL_MINUTES')}
                          type="number"
                          value={String(drafts.NOVA_POSHTA_PRICE_CACHE_TTL_MINUTES ?? '')}
                          onChange={(value) => updateDraft('NOVA_POSHTA_PRICE_CACHE_TTL_MINUTES', Number(value))}
                          min={1}
                        />
                        <TextField
                          autoComplete="off"
                          label={settingLabel('NOVA_POSHTA_PRICE_CACHE_STALE_MINUTES')}
                          type="number"
                          value={String(drafts.NOVA_POSHTA_PRICE_CACHE_STALE_MINUTES ?? '')}
                          onChange={(value) => updateDraft('NOVA_POSHTA_PRICE_CACHE_STALE_MINUTES', Number(value))}
                          min={30}
                        />
                        <TextField
                          autoComplete="off"
                          label="Fallback відділення, грн"
                          type="number"
                          value={centsToUahInput(drafts.NOVA_POSHTA_FALLBACK_BRANCH_PRICE_CENTS)}
                          onChange={(value) => updateDraft('NOVA_POSHTA_FALLBACK_BRANCH_PRICE_CENTS', uahInputToCents(value))}
                          min={0}
                        />
                        <TextField
                          autoComplete="off"
                          label="Fallback поштомат, грн"
                          type="number"
                          value={centsToUahInput(drafts.NOVA_POSHTA_FALLBACK_POSTOMAT_PRICE_CENTS)}
                          onChange={(value) => updateDraft('NOVA_POSHTA_FALLBACK_POSTOMAT_PRICE_CENTS', uahInputToCents(value))}
                          min={0}
                        />
                        <TextField
                          autoComplete="off"
                          label="Fallback курʼєр, грн"
                          type="number"
                          value={centsToUahInput(drafts.NOVA_POSHTA_FALLBACK_COURIER_PRICE_CENTS)}
                          onChange={(value) => updateDraft('NOVA_POSHTA_FALLBACK_COURIER_PRICE_CENTS', uahInputToCents(value))}
                          min={0}
                        />
                      </div>
                      <div>
                        <Text as="h3" variant="headingSm">Автоповтор помилок ТТН</Text>
                        <Text as="p" variant="bodySm" tone="subdued">
                          Worker повертає в retry тільки свіжі помилки звʼязку або rate limit без номера ТТН. Старі замовлення, архів і створені ТТН не чіпаються.
                        </Text>
                      </div>
                      <Checkbox
                        label={settingLabel('NOVA_POSHTA_TTN_AUTO_RETRY_FAILED_ENABLED')}
                        checked={drafts.NOVA_POSHTA_TTN_AUTO_RETRY_FAILED_ENABLED !== false}
                        onChange={(checked) => updateDraft('NOVA_POSHTA_TTN_AUTO_RETRY_FAILED_ENABLED', checked)}
                      />
                      <div className="admin-grid admin-grid--three">
                        <TextField
                          autoComplete="off"
                          label={settingLabel('NOVA_POSHTA_TTN_AUTO_RETRY_FAILED_MAX_AGE_HOURS')}
                          type="number"
                          value={String(drafts.NOVA_POSHTA_TTN_AUTO_RETRY_FAILED_MAX_AGE_HOURS ?? '')}
                          onChange={(value) => updateDraft('NOVA_POSHTA_TTN_AUTO_RETRY_FAILED_MAX_AGE_HOURS', Number(value))}
                          min={1}
                        />
                        <TextField
                          autoComplete="off"
                          label={settingLabel('NOVA_POSHTA_TTN_AUTO_RETRY_FAILED_DELAY_MINUTES')}
                          type="number"
                          value={String(drafts.NOVA_POSHTA_TTN_AUTO_RETRY_FAILED_DELAY_MINUTES ?? '')}
                          onChange={(value) => updateDraft('NOVA_POSHTA_TTN_AUTO_RETRY_FAILED_DELAY_MINUTES', Number(value))}
                          min={1}
                        />
                        <TextField
                          autoComplete="off"
                          label={settingLabel('NOVA_POSHTA_TTN_AUTO_RETRY_FAILED_BATCH_SIZE')}
                          type="number"
                          value={String(drafts.NOVA_POSHTA_TTN_AUTO_RETRY_FAILED_BATCH_SIZE ?? '')}
                          onChange={(value) => updateDraft('NOVA_POSHTA_TTN_AUTO_RETRY_FAILED_BATCH_SIZE', Number(value))}
                          min={1}
                        />
                      </div>
                      <details className="admin-advanced-details">
                        <summary>Службові поля для ТТН</summary>
                        <Text as="p" variant="bodySm" tone="subdued">
                          Платник доставки у custom checkout визначається автоматично за порогом вище. Ці поля лишаються як технічний fallback для майбутнього payload ТТН.
                        </Text>
                        <div className="admin-grid admin-grid--two">
                          <MenuSelect
                            label={settingLabel('NOVA_POSHTA_DELIVERY_PAYER_TYPE')}
                            options={PAYER_TYPE_OPTIONS}
                            value={String(drafts.NOVA_POSHTA_DELIVERY_PAYER_TYPE || 'Recipient')}
                            onChange={(value) => updateDraft('NOVA_POSHTA_DELIVERY_PAYER_TYPE', value)}
                          />
                          <MenuSelect
                            label={settingLabel('NOVA_POSHTA_PAYMENT_METHOD')}
                            options={PAYMENT_FORM_OPTIONS}
                            value={String(drafts.NOVA_POSHTA_PAYMENT_METHOD || 'Cash')}
                            onChange={(value) => updateDraft('NOVA_POSHTA_PAYMENT_METHOD', value)}
                          />
                          <MenuSelect
                            label={settingLabel('NOVA_POSHTA_SENDER_DISPATCH_TYPE')}
                            options={SENDER_DISPATCH_OPTIONS}
                            value={String(drafts.NOVA_POSHTA_SENDER_DISPATCH_TYPE || 'warehouse')}
                            onChange={(value) => updateDraft('NOVA_POSHTA_SENDER_DISPATCH_TYPE', value)}
                          />
                        </div>
                      </details>
                    </div>
                  </Card>

                  <Card>
                    <div className="admin-stack">
                      <div>
                        <Text as="h2" variant="headingMd">Параметри вантажу і API</Text>
                        <Text as="p" variant="bodyMd" tone="subdued">
                          Значення за замовчуванням використовуються, коли замовлення не дає точну вагу, габарити або опис.
                        </Text>
                      </div>
                      <MenuSelect
                        label={settingLabel('NOVA_POSHTA_CARGO_TYPE')}
                        options={CARGO_TYPE_OPTIONS}
                        value={String(drafts.NOVA_POSHTA_CARGO_TYPE || 'Parcel')}
                        onChange={(value) => updateDraft('NOVA_POSHTA_CARGO_TYPE', value)}
                      />
                      <div className="admin-grid admin-grid--two">
                        <TextField
                          autoComplete="off"
                          label={settingLabel('NOVA_POSHTA_DEFAULT_WEIGHT_KG')}
                          type="number"
                          value={String(drafts.NOVA_POSHTA_DEFAULT_WEIGHT_KG ?? '')}
                          onChange={(value) => updateDraft('NOVA_POSHTA_DEFAULT_WEIGHT_KG', Number(value))}
                        />
                        <TextField
                          autoComplete="off"
                          label={settingLabel('NOVA_POSHTA_DEFAULT_SEATS_AMOUNT')}
                          type="number"
                          value={String(drafts.NOVA_POSHTA_DEFAULT_SEATS_AMOUNT ?? '')}
                          onChange={(value) => updateDraft('NOVA_POSHTA_DEFAULT_SEATS_AMOUNT', Number(value))}
                        />
                        <TextField
                          autoComplete="off"
                          label={settingLabel('NOVA_POSHTA_DEFAULT_LENGTH_CM')}
                          type="number"
                          value={String(drafts.NOVA_POSHTA_DEFAULT_LENGTH_CM ?? '')}
                          onChange={(value) => updateDraft('NOVA_POSHTA_DEFAULT_LENGTH_CM', Number(value))}
                        />
                        <TextField
                          autoComplete="off"
                          label={settingLabel('NOVA_POSHTA_DEFAULT_WIDTH_CM')}
                          type="number"
                          value={String(drafts.NOVA_POSHTA_DEFAULT_WIDTH_CM ?? '')}
                          onChange={(value) => updateDraft('NOVA_POSHTA_DEFAULT_WIDTH_CM', Number(value))}
                        />
                        <TextField
                          autoComplete="off"
                          label={settingLabel('NOVA_POSHTA_DEFAULT_HEIGHT_CM')}
                          type="number"
                          value={String(drafts.NOVA_POSHTA_DEFAULT_HEIGHT_CM ?? '')}
                          onChange={(value) => updateDraft('NOVA_POSHTA_DEFAULT_HEIGHT_CM', Number(value))}
                        />
                        <TextField
                          autoComplete="off"
                          label={settingLabel('NOVA_POSHTA_DEFAULT_COST_CENTS')}
                          type="number"
                          value={String(drafts.NOVA_POSHTA_DEFAULT_COST_CENTS ?? '')}
                          onChange={(value) => updateDraft('NOVA_POSHTA_DEFAULT_COST_CENTS', Number(value))}
                        />
                        <TextField
                          autoComplete="off"
                          label={settingLabel('NOVA_POSHTA_TIMEOUT_MS')}
                          type="number"
                          value={String(drafts.NOVA_POSHTA_TIMEOUT_MS ?? '')}
                          onChange={(value) => updateDraft('NOVA_POSHTA_TIMEOUT_MS', Number(value))}
                        />
                      </div>
                      <TextField
                        autoComplete="off"
                        label={settingLabel('NOVA_POSHTA_DEFAULT_DESCRIPTION')}
                        value={String(drafts.NOVA_POSHTA_DEFAULT_DESCRIPTION || '')}
                        onChange={(value) => updateDraft('NOVA_POSHTA_DEFAULT_DESCRIPTION', value)}
                      />
                      <TextField
                        autoComplete="off"
                        label={settingLabel('NOVA_POSHTA_API_URL')}
                        value={String(drafts.NOVA_POSHTA_API_URL || '')}
                        onChange={(value) => updateDraft('NOVA_POSHTA_API_URL', value)}
                      />
                      <div className="admin-inline-actions">
                        <Button variant="primary" loading={savingRules} onClick={saveRules}>
                          Зберегти правила
                        </Button>
                      </div>
                    </div>
                  </Card>
                </>
              ) : null}
            </div>
          </fieldset>
        ) : null}
      </div>
    </Page>
  );
}
